import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PostProjectImageCard from "./components/PostProjectImageCard/PostProjectImageCard";
import ProjectInfoForm from "./components/ProjectInfoForm/ProjectInfoForm";
import RoleDescriptions from "./components/CreateRoleSection/RoleDescriptions";

import validateOneLiner from "../../components/codiOS/utils/validateOneLiner";
import validateProjectDescription from "../../components/codiOS/utils/validateProjectDescription";
import validateRoleDescription from "../../components/codiOS/utils/validateRoleDescription";
import { validateWithCodi } from "../../components/codiOS/utils/validateWithCodi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams();
  const [email, setEmail] = useState("");
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const token = localStorage.getItem("token");
  const extractedUsername = user?.email ? user.email.split("@")[0] : null;

  // Extract communityId from URL query params
  const searchParams = new URLSearchParams(location.search);
  const communityId = searchParams.get("communityId");

  const [image, setImage] = useState(null);
  const [roles, setRoles] = useState([]);
  const [descriptions, setDescriptions] = useState({});
  const [formData, setFormData] = useState({ title: "", oneLiner: "", description: "", category: "", tags: [] });
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchUserProfile = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/auth/profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.email) {
        const extractedUsername = data.username || data.email.split("@")[0];
        const updatedUser = { ...data, username: extractedUsername };
        setUser(updatedUser);
        localStorage.setItem("userEmail", data.email);
      } else navigate("/login");
    } catch {
      navigate("/login");
    }
  };

  useEffect(() => {
    const fetchEmail = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${BACKEND_URL}/auth/user`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok && data.email) {
          setEmail(data.email);
          fetchUserProfile();
        } else navigate("/login");
      } catch {
        navigate("/login");
      }
    };
    fetchEmail();
  }, [token]);

  const addRole = (role) => !roles.includes(role) && setRoles([...roles, role]);

  const handleDescriptionChange = async (role, value) => {
  setDescriptions((prev) => ({ ...prev, [role]: value }));

  // Validate immediately
  const lengthCheck = validateRoleDescription(value);
  const meaningCheck = await validateWithCodi(value);
  setErrors((prev) => ({
    ...prev,
    roleDescriptionsDetail: {
      ...(prev.roleDescriptionsDetail || {}),
      [role]: !lengthCheck.valid
        ? lengthCheck.reason === "too short"
          ? "Role description is too short."
          : "Role description is too long."
        : !meaningCheck.valid
        ? meaningCheck.reason || "Role description lacks clarity."
        : null,
    },
  }));
};

  const handleRemoveRole = (roleToRemove) => {
    setRoles((prev) => prev.filter((r) => r !== roleToRemove));
    setDescriptions((prev) => {
      const updated = { ...prev };
      delete updated[roleToRemove];
      return updated;
    });
  };

  const handleImageUpload = (file) => {
    if (!file) return setErrors((prev) => ({ ...prev, image: "Please upload an image" }));
    setImage(file);
    setErrors((prev) => ({ ...prev, image: null }));
  };

  const validateFields = async () => {
  const newErrors = {};

  // 🧠 Title
  if (!formData.title.trim()) {
    newErrors.title = "Please enter a project title.";
  }

  // ✏️ One-liner
  const oneLinerCheck = validateOneLiner(formData.oneLiner || "");
  if (!oneLinerCheck.valid) {
    newErrors.oneLiner =
      oneLinerCheck.reason === "too short"
        ? "One-liner is too short."
        : "One-liner is too long.";
  } else {
    const oneLinerMeaning = await validateWithCodi(formData.oneLiner);
    if (!oneLinerMeaning.valid)
      newErrors.oneLiner = oneLinerMeaning.reason || "One-liner lacks clarity.";
  }

  // 📝 Description
  const descCheck = validateProjectDescription(formData.description || "");
  if (!descCheck.valid) {
    newErrors.description =
      descCheck.reason === "too short"
        ? "Project description is too short."
        : "Project description is too long.";
  } else {
    const descMeaning = await validateWithCodi(formData.description);
    if (!descMeaning.valid)
      newErrors.description = descMeaning.reason || "Description lacks clarity.";
  }

  // 📂 Category
  if (!formData.category) {
    newErrors.category = "Please select a category.";
  }

  // 🏷️ Tags
  if (!formData.tags.length) {
    newErrors.tags = "Please add at least one tag.";
  }

  // 🖼️ Image
  if (!image) {
    newErrors.image = "Please upload an image.";
  }

  // 👥 Role Descriptions
  const roleErrors = {};
const invalidRoles = await Promise.all(
  roles.map(async (role) => {
    const raw = descriptions[role] || "";
    const lengthCheck = validateRoleDescription(raw);
    const meaningCheck = await validateWithCodi(raw);
    if (!lengthCheck.valid) {
      roleErrors[role] = lengthCheck.reason === "too short"
        ? "Role description is too short."
        : "Role description is too long.";
      return role;
    } else if (!meaningCheck.valid) {
      roleErrors[role] = meaningCheck.reason || "Role description lacks clarity.";
      return role;
    }
    return null;
  })
);
const failedRoles = invalidRoles.filter(Boolean);
if (failedRoles.length > 0) {
  newErrors.roleDescriptions = "Some roles have invalid descriptions.";
  newErrors.roleDescriptionsDetail = roleErrors;
}


  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};



  const handleFinalSubmit = async () => {
    setShowErrors(true);
    if (!validateFields()) return;
    const token = localStorage.getItem("token");
    if (!token) return alert("Unauthorized: Please log in.");
    let projectImageBase64 = null;
    if (image) {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      await new Promise((resolve) => (reader.onloadend = resolve));
      projectImageBase64 = reader.result.split(",")[1];
    } else return alert("Image is required.");

    const projectData = { ...formData, owner: email, image: projectImageBase64 };
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/projects/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(projectData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      for (const roleTitle of roles) {
        const roleDetails = descriptions[roleTitle] || {};

        const res = await fetch(`${BACKEND_URL}/applications/${data.projectId}/add-role`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            roleTitle,
            timeCommitment: roleDetails.timeCommitment || null,
            skills: roleDetails.skills || "",
            description: roleDetails.description || "",
            questions: [
            roleDetails.question1 || "",
            roleDetails.question2 || "",
          ].filter(Boolean), // only push if not empty
        }),
      });

  const roleData = await res.json();
  if (!res.ok) throw new Error(roleData.message);
}

      // If communityId is present, add project to community
      if (communityId) {
        try {
          const communityRes = await fetch(
            `${BACKEND_URL}/communities/${communityId}/projects`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ projectId: data.projectId }),
            }
          );
          
          if (!communityRes.ok) {
            const communityData = await communityRes.json();
            console.error("Failed to add project to community:", communityData.message);
            // Don't fail the whole operation, just log it
          } else {
            console.log("✅ Project successfully added to community");
          }
        } catch (err) {
          console.error("Error adding project to community:", err);
          // Don't fail the whole operation
        }
      }

      const successMessage = communityId 
        ? "Project created and added to community successfully!" 
        : "Project and roles created successfully!";
      alert(successMessage);
      localStorage.setItem("justCreatedNoti", projectData.title);
      
      // Navigate back to community if it was created from there
      if (communityId) {
        setTimeout(() => navigate(`/communities/${communityId}`), 800);
      } else {
        setTimeout(() => navigate(`/${user?.username || extractedUsername}/explore`), 800);
      }
    } catch (err) {
      console.error("Submission error:", err);
      if (err.message.includes("token")) navigate("/login");
      else alert(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-4 py-6 md:px-10 md:py-8 max-w-[1440px] mx-auto">
      {communityId && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm font-medium">
            📍 Creating project for community
          </p>
          <p className="text-blue-600 text-xs mt-1">
            This project will be automatically added to the community after creation
          </p>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-10">
        <div className="md:w-1/2 w-full flex flex-col gap-6">
          <PostProjectImageCard onImageUpload={handleImageUpload} />
          {showErrors && errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
          <RoleDescriptions
            roles={roles}
            descriptions={descriptions}
            onChange={handleDescriptionChange}
            onRemove={handleRemoveRole}
            errors={errors.roleDescriptionsDetail || {}}
          />
          <div className="pt-2">
            <button
              className="w-full bg-black hover:bg-zinc-800 text-white font-semibold py-3 px-6 rounded-xl shadow-md transition duration-200 disabled:opacity-50"
              onClick={handleFinalSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <p className="text-sm">Creating project...</p>
                  </div>
              ) : (
                "Create"
              )}
            </button>
          </div>
        </div>

        <div className="md:w-1/2 w-full">
          <ProjectInfoForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            showErrors={showErrors}
            addRole={addRole}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateProject;